import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  TemplateChannel,
  TemplateContext,
  TemplateLibraryEntry,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

interface TemplateLibraryListFilters {
  channel?: TemplateChannel;
  context?: TemplateContext;
}

/** Shared query key root so actions can invalidate the whole library at once. */
export const templateLibraryQueryKey = (brandId: string) => ['templateLibrary', brandId] as const;

const buildListPath = (brandId: string, filters: TemplateLibraryListFilters): string => {
  const params = new URLSearchParams();
  if (filters.channel) {
    params.set('channel', filters.channel);
  }
  if (filters.context) {
    params.set('context', filters.context);
  }
  const query = params.toString();
  return `/dashboard/brands/${brandId}/templates${query ? `?${query}` : ''}`;
};

const fetchTemplates = async (
  brandId: string,
  filters: TemplateLibraryListFilters
): Promise<TemplateLibraryEntry[]> => {
  const { data } = await api.get<ApiResponse<TemplateLibraryEntry[]>>(
    buildListPath(brandId, filters)
  );
  return data.data ?? [];
};

/**
 * List the brand's templates. Server-side channel/context filtering is wired up
 * for completeness, but the page passes no filters and narrows client-side
 * because the payload is small and unpaginated. The query key includes the
 * filters so distinct filter combinations cache independently; actions
 * invalidate the whole library via the `templateLibraryQueryKey` prefix.
 */
export const useTemplateLibraryQuery = (
  brandId: string,
  filters: TemplateLibraryListFilters = {}
) =>
  useQuery<TemplateLibraryEntry[], Error>({
    queryKey: [
      ...templateLibraryQueryKey(brandId),
      filters.channel ?? null,
      filters.context ?? null,
    ],
    queryFn: () => fetchTemplates(brandId, filters),
    enabled: !!brandId,
  });
